import { useEffect, useState } from 'react'
import {
  LICENSE_STATE_LABEL,
  ORG_PLANS,
  ORG_PLAN_ORDER,
  formatBytes,
  type AdminOrgDto,
  type OrgPlan,
} from '@praesto/shared'
import { api, ApiError } from '../api.js'
import { Dialog } from './Dialog.js'
import { useLanguage, useText } from '../i18n.js'

/**
 * Alle Kunden auf einen Blick — die kaufmännische Ansicht des Betreibers.
 *
 * Sie zeigt Namen, Paket, Laufzeit, Belegung und Ansprechpartner. Sie zeigt
 * keine Boards: Die Betreiberebene nimmt Organisationen auf und rechnet ab,
 * sie schaut nicht in fremde Arbeit.
 */
export function AdminDialog({ onClose }: { onClose: () => void }): React.ReactElement {
  const t = useText()
  const { language } = useLanguage()
  const [orgs, setOrgs] = useState<AdminOrgDto[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [note, setNote] = useState<string | null>(null)
  const [newName, setNewName] = useState('')
  const [newPlan, setNewPlan] = useState<OrgPlan>('team')
  const [newUntil, setNewUntil] = useState(inOneYear())
  const [busy, setBusy] = useState(false)

  async function reload(): Promise<void> {
    setOrgs(await api.adminOrgs())
  }

  useEffect(() => {
    api.adminOrgs().then(setOrgs).catch(() => setError(t('admin.loadFailed')))
  }, [])

  async function change(
    org: AdminOrgDto,
    body: Parameters<typeof api.setLicense>[1],
  ): Promise<void> {
    setError(null)
    try {
      await api.setLicense(org.id, body)
      await reload()
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : t('admin.changeFailed'))
    }
  }

  async function create(event: React.FormEvent): Promise<void> {
    event.preventDefault()
    setError(null)
    setBusy(true)
    try {
      await api.createOrg(newName.trim(), newPlan, newUntil || null)
      setNewName('')
      await reload()
      setNote(
        `„${newName.trim()}" angelegt. Jetzt in der Organisation die erste Person einladen.`,
      )
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : t('admin.createFailed'))
    } finally {
      setBusy(false)
    }
  }

  async function remove(org: AdminOrgDto): Promise<void> {
    const typed = window.prompt(
      t('admin.deletePrompt', {
        org: org.name,
        boards: t.plural(org.boardCount, 'admin.board.one', 'admin.board.many'),
        storage: formatBytes(org.storageBytes, language),
        members: org.memberCount,
      }),
    )
    if (typed === null) return
    setError(null)
    try {
      await api.deleteOrg(org.id, typed)
      await reload()
      setNote(t('admin.deleted', { org: org.name }))
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : t('admin.deleteFailed'))
    }
  }

  return (
    <Dialog title={t('boards.customers')} onClose={onClose}>
      {orgs === null && !error && <p className="muted small">{t('common.loading')}</p>}

      <ul className="admin-list">
        {orgs?.map((org) => {
          const spec = ORG_PLANS[org.license.plan]
          const overMembers = org.memberCount > org.license.memberLimit
          const overStorage = org.storageBytes > org.license.storageLimitBytes
          return (
            <li key={org.id} className={`admin-org is-${org.license.state}`}>
              <div className="admin-org-head">
                <span className="admin-org-name">{org.name}</span>
                <span className={`badge state-${org.license.state}`}>
                  {t(LICENSE_STATE_LABEL[org.license.state])}
                  {org.license.daysLeft !== null &&
                    org.license.state !== 'aktiv' &&
                    ` · ${
                      org.license.daysLeft >= 0
                        ? t('admin.daysLeft', { days: org.license.daysLeft })
                        : t('admin.daysSince', { days: -org.license.daysLeft })
                    }`}
                </span>
              </div>

              <div className="admin-org-meta muted small">
                {org.admins.length > 0
                  ? org.admins.map((a) => a.email).join(', ')
                  : t('admin.noAdmins')}
              </div>

              <div className="admin-org-figures">
                <span className={overMembers ? 'over' : undefined}>
                  {org.memberCount} / {org.license.memberLimit} {t('admin.people')}
                </span>
                <span className={overStorage ? 'over' : undefined}>
                  {formatBytes(org.storageBytes, language)} /{' '}
                  {formatBytes(org.license.storageLimitBytes, language)}
                </span>
                <span>
                  {t.plural(org.boardCount, 'admin.board.one', 'admin.board.many')}
                </span>
                <span title={t('admin.priceTitle')}>
                  {spec.monthlyEur === null
                    ? t('admin.onRequest')
                    : t('signup.perMonth', { price: spec.monthlyEur })}
                </span>
              </div>

              <div className="admin-org-controls">
                <label className="inline-field">
                  <span>{t('signup.plan')}</span>
                  <select
                    value={org.license.plan}
                    onChange={(event) =>
                      void change(org, { plan: event.target.value as OrgPlan })
                    }
                  >
                    {ORG_PLAN_ORDER.map((plan) => (
                      <option key={plan} value={plan}>
                        {t(ORG_PLANS[plan].label)}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="inline-field">
                  <span>{t('admin.until')}</span>
                  <input
                    type="date"
                    value={org.license.until ?? ''}
                    onChange={(event) =>
                      void change(org, { licensedUntil: event.target.value || null })
                    }
                    title={t('admin.untilTitle')}
                  />
                </label>

                <button
                  className="button ghost"
                  onClick={() => void change(org, { licensedUntil: plusYear(org.license.until) })}
                  title={t('admin.extendTitle')}
                >
                  {t('admin.extend')}
                </button>

                {/*
                  Löschen erscheint erst, wenn die Frist wirklich um ist. Ein
                  Knopf, der 21 Tage lang nur „geht noch nicht" sagt, lädt zum
                  Ausprobieren ein — und irgendwann geht es dann eben doch.
                */}
                {org.license.state === 'loeschung-faellig' && (
                  <button className="button ghost danger" onClick={() => void remove(org)}>
                    {t('common.delete')}
                  </button>
                )}
              </div>
            </li>
          )
        })}
      </ul>

      <form className="member-add" onSubmit={create}>
        <h4 className="dialog-section">{t('admin.addCustomer')}</h4>
        <label className="field">
          <span>{t('team.orgName')}</span>
          <input
            value={newName}
            onChange={(event) => setNewName(event.target.value)}
            required
            placeholder={t('admin.orgPlaceholder')}
          />
        </label>
        <label className="field">
          <span>{t('signup.plan')}</span>
          <select value={newPlan} onChange={(event) => setNewPlan(event.target.value as OrgPlan)}>
            {ORG_PLAN_ORDER.map((plan) => (
              <option key={plan} value={plan}>
                {t(ORG_PLANS[plan].label)} —{' '}
                {t('signup.planSummary', {
                  members: ORG_PLANS[plan].members,
                  storage: formatBytes(ORG_PLANS[plan].storageBytes, language),
                })}
                {ORG_PLANS[plan].monthlyEur !== null &&
                  `, ${t('signup.perMonth', { price: ORG_PLANS[plan].monthlyEur ?? 0 })}`}
              </option>
            ))}
          </select>
          <small>{t(ORG_PLANS[newPlan].hint)}</small>
        </label>
        <label className="field">
          <span>{t('admin.until')}</span>
          <input type="date" value={newUntil} onChange={(event) => setNewUntil(event.target.value)} />
          <small>{t('admin.untilHint')}</small>
        </label>
        <button className="button primary" type="submit" disabled={busy}>
          {busy ? t('common.moment') : t('team.add')}
        </button>
      </form>

      {note && <p className="muted small">{note}</p>}
      {error && <p className="form-error">{error}</p>}
    </Dialog>
  )
}

/** `YYYY-MM-DD` in einem Jahr — die übliche Vertragslaufzeit als Vorbelegung. */
function inOneYear(): string {
  const date = new Date()
  date.setFullYear(date.getFullYear() + 1)
  return date.toISOString().slice(0, 10)
}

/**
 * Ein Jahr auf die bestehende Laufzeit — oder auf heute, wenn sie schon
 * vorbei ist. Andernfalls würde eine Verlängerung nach langem Verzug im
 * Abgelaufenen landen und der Kunde wäre nach dem Klick immer noch gesperrt.
 */
function plusYear(until: string | null): string {
  const base = until ? new Date(until) : new Date()
  const from = base.getTime() > Date.now() ? base : new Date()
  from.setFullYear(from.getFullYear() + 1)
  return from.toISOString().slice(0, 10)
}
